import { Outlet, createFileRoute, notFound } from '@tanstack/react-router';

import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { useBesluitId } from '@/api/queries/besluit';
import { useBesluitTypeId } from '@/api/queries/besluitType';
import { DocumentTitle } from '@/components/DocumentTitle';
import { eStrings } from '@/constants/strings';
import { isUserAuthenticatedForRoute } from '@/stores/user/isUserAuthenticatedForRoute';

import { Navigation } from './-components/Navigation/Navigation';
import { SidebarLeft } from './-components/SidebarLeft/SidebarLeft';
import { useEditorStore } from './-store/useEditorStore';
import {
  ensureHoofdstukkenInEditorQueryData,
  useHoofdstukkenInEditor,
} from './-utils/useHoofdstukkenInEditor';

/**
 * Route
 */
export const Route = createFileRoute('/$bedrijfLineageId/editor/$besluitId')({
  beforeLoad: async ({ preload }) => {
    await isUserAuthenticatedForRoute({
      preload,
      allowed: ['Vergunningverlener'],
    });
  },
  loader: async ({
    context: { queryClient },
    params: { besluitId, bedrijfLineageId },
  }) => {
    const [besluit, latestBedrijf] = await Promise.all([
      queryClient.ensureQueryData(useBesluitId.getOptions(besluitId)),
      queryClient.ensureQueryData(
        useLatestBedrijf.getOptions(bedrijfLineageId)
      ),
      ensureHoofdstukkenInEditorQueryData({ besluitId, queryClient }),
    ]);

    await queryClient.ensureQueryData(
      useBesluitTypeId.getOptions(besluit.Type)
    );

    if (!besluit || !latestBedrijf.objects?.[0]) throw notFound();
  },
  onLeave: () => {
    // Reset the editor state when the user leaves the editor
    const { reset } = useEditorStore.getState();
    reset();
  },
  component: EditorComponent,
});

/**
 * Route component
 */
function EditorComponent() {
  const { besluitId, bedrijfLineageId } = Route.useParams();

  const besluit = useBesluitId(besluitId);
  const bedrijf = useLatestBedrijf(bedrijfLineageId);
  const besluitType = useBesluitTypeId(besluit.data?.Type ?? '', {
    enabled: !!besluit.data?.Type,
  });
  const hoofdstukkenInEditor = useHoofdstukkenInEditor(besluitId);

  if (
    !bedrijf.data ||
    !besluit.data ||
    !besluitType.data ||
    !hoofdstukkenInEditor.data
  )
    return null;

  return (
    <>
      <DocumentTitle sections={[eStrings.LBL_DIGI_V, 'Editor']} />

      <Navigation besluit={besluit.data} bedrijf={bedrijf.data} />

      <div className="flex">
        <SidebarLeft
          besluit={besluit.data}
          hoofdstukken={hoofdstukkenInEditor.data}
        />

        <main
          className="w-full bg-gray-50"
          // For testing purposes
          data-initieel-besluit={!!besluitType.data?.Initieel}
        >
          <Outlet />
        </main>
      </div>
    </>
  );
}
